import Vue from 'vue';
import {ApckImpersonationBanner} from "../components/auth";
import {GetToken, SetToken} from "./jwt";
import { toastMessage } from '../components/_ui/_utils/useEventUtils'

// Default authentication configuration
const defaultAuthConfig = {
    loginRoute: '/spa-auth/',
    loginRouteName: null,        // Named route for login (when using Vue Router)
    refreshRoute: '/security/refresh',
    logoutRoute: '/security/logout',
    useReturnUrl: true,          // Whether to include return URLs in redirect
    useRouter: false,            // Whether to use Vue Router for navigation
    dashboardRouteName: null     // Named route for dashboard (for post-login navigation)
};

// Global authentication configuration that can be overridden
let authConfig = { ...defaultAuthConfig };

/**
 * Reimplementation of jQuery's $.param() so query strings and
 * x-www-form-urlencoded request bodies are serialized byte-for-byte the way
 * jQuery did it:
 *   - traditional mode arrays    -> key=a&key=b&key=c
 *   - non-traditional arrays     -> key[]=a&key[]=b
 *   - non-traditional objects    -> key[sub]=v   (deep / recursive)
 *
 * Deviation from jQuery: null / undefined values are dropped entirely rather
 * than serialized as an empty `key=` value, to keep optional params out of the
 * request. Empty strings ('') are preserved as `key=`.
 */
function jqueryParam(data, traditional) {
    const parts = [];
    const rbracket = /\[\]$/;
    const add = (key, value) => {
        const v = typeof value === 'function' ? value() : value;
        // Drop null/undefined; '' and other falsy-but-real values are kept.
        if (v == null) return;
        parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(v));
    };
    const buildParams = (prefix, obj) => {
        if (Array.isArray(obj)) {
            obj.forEach((v, i) => {
                if (traditional || rbracket.test(prefix)) {
                    // Treat each array item as a scalar.
                    add(prefix, v);
                } else {
                    // Item is non-scalar (array or object): encode its numeric index.
                    buildParams(
                        prefix + '[' + (typeof v === 'object' && v != null ? i : '') + ']',
                        v
                    );
                }
            });
        } else if (!traditional && obj != null && typeof obj === 'object') {
            for (const name in obj) {
                buildParams(prefix + '[' + name + ']', obj[name]);
            }
        } else {
            add(prefix, obj);
        }
    };
    for (const prefix in data) {
        buildParams(prefix, data[prefix]);
    }
    return parts.join('&');
}

/**
 * Accept headers jQuery sends per dataType. jQuery appends `*\/*; q=0.01`
 * to the type-specific accepts; an unknown / unset dataType falls back to `*\/*`.
 */
const ACCEPT_HEADERS = {
    json: 'application/json, text/javascript, */*; q=0.01',
    xml: 'application/xml, text/xml, */*; q=0.01',
    html: 'text/html, */*; q=0.01',
    text: 'text/plain, */*; q=0.01',
    script: 'text/javascript, application/javascript, application/ecmascript, application/x-ecmascript, */*; q=0.01',
};

/**
 * Whether a request URL targets a different origin than the current page.
 * Mirrors jQuery's crossDomain detection: relative / same-origin URLs are
 * NOT cross-origin. Used to decide whether to send X-Requested-With, which
 * jQuery only adds for same-origin requests.
 */
function isCrossOrigin(requestUrl) {
    try {
        return new URL(requestUrl, window.location.href).origin !== window.location.origin;
    } catch (e) {
        // Relative or unparseable URL -> treat as same-origin (jQuery's default).
        return false;
    }
}

/**
 * Build request headers mirroring jQuery's header conventions:
 * caller-supplied headers from opts, JWT Authorization,
 * X-Requested-With for same-origin, Accept derived from dataType,
 * and caller-specified Content-Type.
 */
function buildHeaders(ajax_opts, opts, url) {
    const headers = { ...(ajax_opts.headers || {}), ...(opts.headers || {}) };

    const has = (name) =>
        Object.keys(headers).some((h) => h.toLowerCase() === name.toLowerCase());

    // JWT token injection
    const jwt = GetToken();
    if (jwt && !has('Authorization')) {
        headers['Authorization'] = 'Bearer ' + jwt;
    }

    // jQuery only sends X-Requested-With for same-origin requests.
    if (!isCrossOrigin(url) && !has('X-Requested-With')) {
        headers['X-Requested-With'] = 'XMLHttpRequest';
    }

    // jQuery sets an Accept header derived from dataType (for content negotiation).
    if (!has('Accept')) {
        headers['Accept'] = ACCEPT_HEADERS[ajax_opts.dataType] || '*/*';
    }

    // Set Content-Type header if provided and not already set
    if (ajax_opts.contentType && !has('Content-Type')) {
        headers['Content-Type'] = ajax_opts.contentType;
    }

    return headers;
}

/**
 * Wrap a native Promise so it also exposes jQuery Deferred's .done/.fail/.always
 * methods. AjaxUtils.ajax() used to return a jQuery promise (from $.ajax().then())
 * a bare fetch() Promise would throw "x.always is not a function". The underlying
 * promise here only ever resolves (HTTP/network errors resolve with an xhr-shaped
 * object), so .fail() never fires (exactly as before) but is provided for
 * completeness.
 */
function makeJqueryLikePromise(promise) {
    return {
        then(onFulfilled, onRejected) {
            return makeJqueryLikePromise(promise.then(onFulfilled, onRejected));
        },
        catch(onRejected) {
            return makeJqueryLikePromise(promise.catch(onRejected));
        },
        finally(onFinally) {
            return makeJqueryLikePromise(promise.finally(onFinally));
        },
        done(fn) {
            promise.then((value) => { if (fn) fn(value); });
            return this;
        },
        fail(fn) {
            promise.then(null, (reason) => { if (fn) fn(reason); });
            return this;
        },
        always(fn) {
            promise.then(
                (value) => { if (fn) fn(value); },
                (reason) => { if (fn) fn(reason); }
            );
            return this;
        },
    };
}

// Optional Vue Router reference
let router = null;

// Function to configure authentication routes
export const configureAuth = (config = {}) => {
    authConfig = { ...defaultAuthConfig, ...config };

    // Store router reference if provided
    if (config.router) {
        router = config.router;
    }
};

function show_impersonation_prompt(xhr) {
    if (window.document.getElementById('impersonationPrompt')) {
        // the prompt is already being displayed
        return;
    }

    const Prompt = Vue.extend(ApckImpersonationBanner);
    const instance = new Prompt({
        propsData: {
            url: xhr.responseJSON.url,
            message: xhr.responseJSON.message,
            prompt: xhr.responseJSON.prompt,
        }
    });
    instance.$mount();
    window.document.body.appendChild(instance.$el);
}

export default class AjaxUtils {
    static resp401Handler(xhr) {
        // If Vue Router is enabled and available, and we have a named route for login
        if (authConfig.useRouter && router && authConfig.loginRouteName) {
            router.push({ name: authConfig.loginRouteName });
        } else {
            // Otherwise use window.location (default behavior)
            if (authConfig.useReturnUrl) {
                // Include the return URL parameter for redirects
                window.location.href = `${authConfig.loginRoute}?return=${encodeURIComponent(window.location.pathname + window.location.search)}`;
            } else {
                // Redirect directly to login page without return URL parameter
                window.location.href = authConfig.loginRoute;
            }
        }
    }

    // Method for handling post-login navigation
    static navigateToDashboard() {
        // If Vue Router is enabled and we have a dashboard route name
        if (authConfig.useRouter && router && authConfig.dashboardRouteName) {
            router.push({ name: authConfig.dashboardRouteName });
        } else {
            // Fall back to window.location
            window.location.href = authConfig.dashboardRoute || '/';
        }
    }

    static ajax({ajax_opts, success, fail, opts}) {
        function do_success(xhr) {
            if (success !== undefined) {
                let data;
                if (ajax_opts.dataType === 'json') {
                    data = xhr.responseJSON;
                } else if (ajax_opts.dataType === 'xml') {
                    data = xhr.responseXML;
                } else {
                    data = xhr.responseText;
                }
                success(data, xhr.status, xhr);
            }
        }

        function do_fail(xhr) {
            if (fail !== undefined) {
                let data;
                if (ajax_opts.dataType === 'json') {
                    data = xhr.responseJSON;
                } else if (ajax_opts.dataType === 'xml') {
                    data = xhr.responseXML;
                } else {
                    data = xhr.responseText;
                }
                fail(data, xhr.status, xhr);
            }
        }

        const default_opts = {
            show_success_flash: true,
            show_error_flash: true,
            // whether to flash error messages on validation errors
            flash_validation_errors: true,
            // whether to flash error messages in general (NOTE: overrides flash_validation_errors)
            flash_form_errors: true,
            flash_403_errors: true,
            flash_404_errors: true,
            redirect_on_401: true,
            no_response_message: 'There was no response data received from the server',
            err_403_message: 'Access to this resource was denied.',
            err_404_message: 'The requested page could not be found',
            success_message: '',
            error_message: '',
        }
        opts = Object.assign({}, default_opts, opts);

        const get_message = function(json) {
            if (json?.message || json?.msg) {
                const trimmed = String(json.message || json.msg).trim();
                if (trimmed) {
                    return trimmed;
                }
            }
        }

        // Build fetch options from jQuery-style ajax_opts
        let jsonParseError = false;

        const buildFetchResponse = async (response) => {
            let data;
            if (ajax_opts.dataType === 'json') {
                const text = await response.text();
                if (text) {
                    try {
                        data = JSON.parse(text);
                    } catch (e) {
                        jsonParseError = true;
                    }
                } else {
                    // Empty body: jQuery yields null for dataType:'json', not undefined.
                    data = null;
                }
            } else if (ajax_opts.dataType === 'xml') {
                const text = await response.text();
                data = new DOMParser().parseFromString(text, 'text/xml');
            } else {
                data = await response.text();
            }

            return {
                status: response.status,
                statusText: response.statusText,
                responseJSON: data,
                responseText: typeof data === 'string' ? data : (data != null ? JSON.stringify(data) : ''),
                responseXML: data instanceof Document ? data : null,
                getResponseHeader: (name) => response.headers.get(name),
            };
        };

        // Normalise method to uppercase so 'get'/'GET'/'Get' all behave identically.
        //
        // `method` is honoured as an alias for `type` because jQuery's $.ajax
        // accepted both, and callers written against it use them interchangeably.
        // Dropping the alias failed silently and in the worst possible way: the
        // verb fell back to GET, so a DELETE or PUT was sent as a GET to a path
        // that only answers the real verb, and the 405 surfaced to the user as a
        // generic "could not do that" with nothing in it pointing at the cause.
        const method = (ajax_opts.type || ajax_opts.method || 'GET').toUpperCase();

        // jQuery only serialises data into the query string for GET/HEAD; every
        // other verb (including DELETE) carries data in the request body.
        const queryInUrl = method === 'GET' || method === 'HEAD';

        // Build the full URL, appending query params (jQuery $.param semantics)
        // for GET/HEAD requests.
        let url = ajax_opts.url;
        if (queryInUrl && ajax_opts.data) {
            const params = jqueryParam(ajax_opts.data, ajax_opts.traditional);
            if (params) {
                url += (url.includes('?') ? '&' : '?') + params;
            }
        }

        const headers = buildHeaders(ajax_opts, opts, url);

        // Case-insensitive header lookup so a caller-supplied header (e.g.
        // 'content-type' / 'accept') is respected and we never emit a duplicate.
        const hasHeader = (name) =>
            Object.keys(headers).some((h) => h.toLowerCase() === name.toLowerCase());

        const fetchOpts = {
            method: method,
            headers: headers,
            credentials: 'same-origin',
        };

        // Handle the request body for verbs that carry one.
        if (!queryInUrl && ajax_opts.data) {
            if (ajax_opts.processData !== false && typeof ajax_opts.data === 'object') {
                // jQuery's processData:true ALWAYS form-encodes non-string data via
                // $.param, regardless of contentType. The request body and the
                // Content-Type header are independent in jQuery.
                fetchOpts.body = jqueryParam(ajax_opts.data, ajax_opts.traditional);
                if (!ajax_opts.contentType && !hasHeader('Content-Type')) {
                    headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
                }
            } else {
                fetchOpts.body = ajax_opts.data;
            }
        }

        // Set Content-Type header if provided and not already set
        if (ajax_opts.contentType && !hasHeader('Content-Type')) {
            headers['Content-Type'] = ajax_opts.contentType;
        }

        return makeJqueryLikePromise(fetch(url, fetchOpts).then(
            async (response) => {
                const xhr = await buildFetchResponse(response);

                // jQuery treats 304 Not Modified as success alongside 2xx.
                if (response.ok || response.status === 304) {
                    if (ajax_opts.dataType === 'json' && jsonParseError) {
                        do_fail(xhr);
                        return xhr;
                    }
                    // status code 2xx - success path
                    if (ajax_opts.dataType === 'json') {
                        const new_jwt = response.headers.get('Authorization');
                        if (new_jwt) {
                            SetToken(new_jwt);
                        }
                        if (xhr.responseJSON?.success === false) {
                            if (opts.show_error_flash) {
                                const message = opts.error_message || get_message(xhr.responseJSON);

                                toastMessage({
                                    type: 'error',
                                    message: message,
                                    errors: xhr.responseJSON?.errors,
                                });
                            }
                            do_fail(xhr);
                            return xhr;
                        } else if (opts.show_success_flash && xhr.responseJSON?.success) {
                            const message = opts.success_message || get_message(xhr.responseJSON);

                            if (message) {
                                toastMessage({
                                    type: 'success',
                                    message: message,
                                });
                            }
                        }
                    }
                    do_success(xhr);
                    return xhr;
                }

                // status code >= 400 - error path (mirrors jQuery's error handler)
                if (xhr.status === 409) {
                    show_impersonation_prompt(xhr);
                    return xhr;
                }
                if (xhr.status === 401 && opts.redirect_on_401) {
                    AjaxUtils.resp401Handler(xhr);
                    return xhr;
                }
                if (xhr.status === 403) {
                    if (opts.flash_form_errors && opts.flash_403_errors) {
                        const json = xhr.responseJSON;
                        toastMessage({
                            type: 'error',
                            message: get_message(json) || opts.err_403_message,
                            errors: json?.errors,
                        });
                    }
                    do_fail(xhr);
                    return xhr;
                }
                if (xhr.status === 404 && opts.flash_form_errors && opts.flash_404_errors) {
                    toastMessage({
                        type: 'error',
                        message: opts.err_404_message,
                        errors: xhr.responseJSON?.errors,
                    });
                    do_fail(xhr);
                    return xhr;
                }
                if (xhr.status >= 400 && (opts.flash_validation_errors && opts.flash_form_errors) && ajax_opts.dataType === 'json') {
                    const json = xhr.responseJSON;
                    toastMessage({
                        type: 'error',
                        message: get_message(json) || opts.no_response_message,
                        errors: json?.errors ? json.errors : []
                    });
                }
                do_fail(xhr);
                return xhr;
            },
            async (error) => {
                // fetch() only rejects on network failures, not HTTP errors
                const xhr = {
                    status: 0,
                    statusText: error.message,
                    responseText: '',
                    getResponseHeader: () => null,
                };
                do_fail(xhr);
                return xhr;
            }
        ));
    }

    static ajax_get({url, query_params, success, fail, opts, traditional}) {
        const ajax_opts = {
            type: "GET",
            url: url,
            data: query_params,
            dataType: opts?.dataType || "json",
            traditional: traditional,
        }
        return AjaxUtils.ajax({
            ajax_opts: ajax_opts,
            success: success,
            fail: fail,
            opts: opts
        });
    }

    static ajax_post({url, data, success, fail, opts}) {
        const ajax_opts = {
            type: "POST",
            url: url,
            data: data,
            dataType: opts?.dataType || "json",
            contentType: opts?.contentType !== undefined ? opts.contentType : "application/json; charset=utf-8",
            processData: opts?.processData !== undefined ? opts.processData : true,
        }
        return AjaxUtils.ajax({
            ajax_opts: ajax_opts,
            success: success,
            fail: fail,
            opts: opts
        });
    }

    static ajax_patch({url, data, success, fail, opts}) {
        const ajax_opts = {
            type: "PATCH",
            url: url,
            data: data,
            dataType: opts?.dataType || "json",
            contentType: opts?.contentType !== undefined ? opts.contentType : "application/json; charset=utf-8",
            processData: opts?.processData !== undefined ? opts.processData : true,
        }
        return AjaxUtils.ajax({
            ajax_opts: ajax_opts,
            success: success,
            fail: fail,
            opts: opts
        });
    }

    static ajax_put({url, data, success, fail, opts}) {
        const ajax_opts = {
            type: "PUT",
            url: url,
            data: data,
            dataType: opts?.dataType || "json",
            contentType: opts?.contentType !== undefined ? opts.contentType : "application/json; charset=utf-8",
            processData: opts?.processData !== undefined ? opts.processData : true,
        }
        return AjaxUtils.ajax({
            ajax_opts: ajax_opts,
            success: success,
            fail: fail,
            opts: opts
        });
    }

    static ajax_delete({url, success, fail, opts}) {
        const ajax_opts = {
            type: "DELETE",
            url: url,
        }
        return AjaxUtils.ajax({
            ajax_opts: ajax_opts,
            success: success,
            fail: fail,
            opts: opts
        });
    }
}
